import type { ButtonHTMLAttributes, ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

const BUTTON_VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-accent hover:bg-brand-accent-hover text-brand-text-inverse font-medium',
  secondary:
    'bg-brand-surface-card border border-brand-border text-brand-text-primary hover:bg-brand-surface',
  danger:
    'bg-brand-priority-urgent hover:opacity-90 text-white font-medium',
  ghost:
    'bg-transparent text-brand-text-primary hover:bg-brand-surface',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  children: ReactNode;
}

export function Button({ variant = 'primary', className = '', children, ...rest }: ButtonProps) {
  return (
    <button
      className={`inline-flex items-center justify-center px-4 py-2 rounded text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${BUTTON_VARIANT_CLASSES[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info';

const BADGE_VARIANT_CLASSES: Record<BadgeVariant, string> = {
  success: 'bg-brand-accent/20 text-brand-text-primary',
  warning: 'bg-brand-priority-high/20 text-brand-text-primary',
  danger:  'bg-brand-priority-urgent/20 text-brand-priority-urgent',
  info:    'bg-brand-surface-card border border-brand-border text-brand-text-secondary',
};

interface BadgeProps {
  variant?: BadgeVariant;
  children: ReactNode;
  className?: string;
}

export function Badge({ variant = 'info', className = '', children }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${BADGE_VARIANT_CLASSES[variant]} ${className}`}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------

type TicketStatus = 'OPEN' | 'IN_PROGRESS' | 'WAITING_ON_CLIENT' | 'RESOLVED' | 'CLOSED';

const STATUS_BADGE_CLASSES: Record<TicketStatus, string> = {
  OPEN:             'bg-brand-status-open-bg text-brand-status-open',
  IN_PROGRESS:      'bg-brand-status-in-progress-bg text-brand-status-in-progress',
  WAITING_ON_CLIENT:'bg-brand-status-waiting-bg text-brand-status-waiting',
  RESOLVED:         'bg-brand-status-resolved-bg text-brand-status-resolved',
  CLOSED:           'bg-brand-status-closed-bg text-brand-status-closed',
};

interface StatusBadgeProps {
  status: TicketStatus;
  className?: string;
}

export function StatusBadge({ status, className = '' }: StatusBadgeProps) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE_CLASSES[status]} ${className}`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Spinner
// ---------------------------------------------------------------------------

interface SpinnerProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const SPINNER_SIZE: Record<string, string> = { sm: 'h-4 w-4', md: 'h-6 w-6', lg: 'h-8 w-8' };

export function Spinner({ className = '', size = 'md' }: SpinnerProps) {
  return (
    <svg
      className={`animate-spin text-brand-accent ${SPINNER_SIZE[size]} ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}
